import React, { useState } from "react";
import FormattedMessage from "components/FormattedMessage";
import map from "lodash/map";
import { Flex, Button, Box, Text } from "ui-components";
import ReactModal from "react-modal";
import { MANAGE_ORDER_TRANSLATION_MAPPINGS } from "utils/constants";
import { urlParam, getActionUrl, sortArrayByPriority } from "utils/helpers";
import { getButtonColorVariant } from "ui-components/theme";
import OTPModal from "components/OTPModal";
import get from "lodash/get";
import has from "lodash/has";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import * as Icons from "components/Icons";

const styles = {
  modalStyle: {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    },
    content: {
      position: "absolute",
      border: "1px solid #ccc",
      WebkitOverflowScrolling: "touch",
      borderRadius: "8px",
      outline: "none",
      padding: "20px",
      maxWidth: "320px",
      height: "fit-content",
      minHeight: "320px",
      margin: "auto"
    }
  }
};

const OrderActionsCard = props => {
  const {
    fetchOrderDetailsAndNavigate,
    orderRef,
    orderActions,
    buttonTheme,
    locale,
    orderActionName,
    planName,
    data,
    onActionClicked,
    appSettings
  } = props;

  const [showModal, setShowModal] = useState(false);

  const closeModal = () => setShowModal(false);
  const [fullyQualifiedURL, setFullyQualifiedURL] = useState("");
  const [anchorTarget, setAnchorTarget] = useState("_self");
  const [showPopupModal, setShowPopupModal] = useState(false);
  const showOnClickPopupList = get(
    appSettings,
    "generic.manage_order_actions_popup",
    []
  );
  const showOnClickPopupPlansList = get(
    appSettings,
    "generic.manage_order_actions_popup_plans",
    []
  );

  const fetchOrderDetailsParam = {
    orderRef,
    sessionToken: "",
    orderActionName,
    closeModal,
    setShowModal,
    locale
  };

  const onOTPSubmitSuccess = sessionToken => {
    fetchOrderDetailsAndNavigate({
      ...fetchOrderDetailsParam,
      fullyQualifiedURL,
      anchorTarget,
      sessionToken
    });
  };

  const onClick = action => {
    const fullyQualifiedURL =
      get(data, ["action_config", action, "url"]) || getActionUrl(action);
    const requireOTPCall = !has(data, ["otp_skip_action_config", action], true);
    const actionType =
      get(data, ["action_config", action, "action_type"]) || action;
    const anchorTarget = get(
      data,
      ["action_config", action, "target"],
      "_self"
    );

    setAnchorTarget(anchorTarget);
    setFullyQualifiedURL(fullyQualifiedURL);
    onActionClicked(
      fetchOrderDetailsParam,
      action,
      actionType,
      anchorTarget,
      fullyQualifiedURL,
      requireOTPCall
    );
  };

  const buttonWidth = get(data, "buttonWidth", [1, 0.8, 0.6]);
  const buttonVariant = get(data, "buttonVariant", "outline");
  const queryParamsConditions = get(data, "query_param_required", {});
  const unsupportedActions = get(data, "unsupportedActions", []);
  // sort order actions based on priority
  const sortedOrderActions = sortArrayByPriority(
    orderActions,
    get(data, "highPriorityActions", []),
    get(data, "lowPriorityActions", [])
  );

  const onClickShowPopup = () => {
    setShowPopupModal(true);
  };

  const hideShowPopup = () => {
    setShowPopupModal(false);
  };

  return (
    <>
      <Flex flexDirection="column" alignItems="center">
        {map(sortedOrderActions, action => {
          const showOnClickPopupPlans = get(showOnClickPopupPlansList, action);
          const checkPlanForOnClickPopup =
            !isEmpty(showOnClickPopupPlansList) && !!planName;
          const showOnClickPopupForPlan = checkPlanForOnClickPopup
            ? includes(showOnClickPopupPlans, planName)
            : true;
          const showOnClickPopupText = showOnClickPopupForPlan
            ? get(showOnClickPopupList, action)
            : false;
          const queryParamRequired = queryParamsConditions[action];
          const allowAction = queryParamRequired
            ? urlParam(queryParamRequired) === "true"
            : true;
          const actionLabel = MANAGE_ORDER_TRANSLATION_MAPPINGS[action];
          const bottomHr =
            get(data, ["action_config", action, "bottomHr"]) || false;
          const iconComponent =
            get(data, ["action_config", action, "icon"]) || false;
          const iconStyles =
            get(data, ["action_config", action, "iconStyles"]) || false;
          const ButtonIconComponent = Icons[iconComponent];

          return !includes(unsupportedActions, action) &&
            allowAction &&
            actionLabel ? (
            <>
              <Button
                key={`action-${action}`}
                data-testid={`order-action-${action}-button`}
                my={1}
                variant={buttonVariant}
                sx={getButtonColorVariant(buttonTheme, buttonVariant)}
                width={buttonWidth}
                onClick={() =>
                  showOnClickPopupText ? onClickShowPopup() : onClick(action)
                }
              >
                <Flex
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="center"
                >
                  {ButtonIconComponent && (
                    <Box style={{ marginRight: 10 }}>
                      {
                        <ButtonIconComponent
                          size={iconStyles?.size}
                          color={iconStyles?.color}
                        />
                      }
                    </Box>
                  )}
                  <FormattedMessage id={actionLabel} />
                </Flex>
              </Button>
              {bottomHr && (
                <hr
                  style={{
                    padding: "1rem",
                    marginTop: "2rem",
                    width: "-webkit-fill-available"
                  }}
                />
              )}
              {showOnClickPopupText && (
                <ReactModal style={styles.modalStyle} isOpen={showPopupModal}>
                  <Box>
                    <Button
                      variant="closeButton"
                      as="a"
                      onClick={hideShowPopup}
                      data-testid="close-model-button"
                    >
                      &times;
                    </Button>
                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}
                    >
                      <Text
                        size="sm"
                        mx={6}
                        mb={8}
                        lineHeight="1.2"
                        style={{ whiteSpace: "pre-line" }}
                      >
                        <FormattedMessage html id={showOnClickPopupText} />
                      </Text>
                      <Flex justifyContent="center">
                        <Button onClick={hideShowPopup}>
                          <FormattedMessage id="manage_order_popup_btn_text" />
                        </Button>
                      </Flex>
                    </Box>
                  </Box>
                </ReactModal>
              )}
            </>
          ) : null;
        })}
      </Flex>

      <OTPModal
        {...props}
        showModal={showModal}
        closeModal={closeModal}
        buttonTheme={buttonTheme}
        onSuccess={onOTPSubmitSuccess}
      />
    </>
  );
};

export default OrderActionsCard;
